import React, { useMemo } from 'react';
import { useSeason } from '../../SeasonContext';
import { computePlayerSessions, groupIntoWeeks, weekTotals } from './playerChronicShared';

// Einstein compose panel for Player Chronic Load (2026-09-10) — same pattern as
// MatchChronicPanel.js/MedicalPanel.js: presentational only, takes a player id plus the shared
// reference data and renders through computePlayerSessions/groupIntoWeeks, the exact pure
// functions PlayerChronic.js's own individual view now uses too. Shows the last `limit` ISO
// weeks' totals — the full page's own default selection (its own auto-select effect picks the 4
// most recent weeks) — not the day-by-day availability grid, which needs its own per-player fetch
// and isn't essential for a compact side-by-side comparison.
const WEEK_LIMIT = 4;
const f0 = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());

export default function PlayerChronicPanel({ playerId, players, sessions, limit = WEEK_LIMIT }) {
  const { selectedSeasons } = useSeason();
  const player = useMemo(() => players.find(p => String(p.id) === String(playerId)), [players, playerId]);

  const weeks = useMemo(() => {
    const playerSessions = computePlayerSessions(playerId, sessions, selectedSeasons);
    return groupIntoWeeks(playerSessions).slice(0, limit);
  }, [playerId, sessions, selectedSeasons, limit]);

  if (!player) {
    return <div style={{ padding: 16, color: 'var(--text-dimmed)', fontSize: 13 }}>No player found for id {playerId}.</div>;
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', minWidth: 300 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{player.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 14 }}>Last {weeks.length || limit} weeks — Chronic Load</div>
      {weeks.length === 0 ? (
        <div style={{ color: 'var(--text-dimmed)', fontSize: 12, padding: '16px 0' }}>No sessions found.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: 'var(--text-dimmed)', fontSize: 10, textTransform: 'uppercase' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Week</th>
              <th style={{ padding: '4px 8px' }}>Sessions</th>
              <th style={{ padding: '4px 8px' }}>TD</th>
              <th style={{ padding: '4px 8px' }}>HID</th>
              <th style={{ padding: '4px 8px' }}>SA</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map(w => {
              const t = weekTotals(w);
              return (
                <tr key={w.key} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-primary)' }}>W{w.weekNum} '{String(w.year).slice(2)}</td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-muted)' }}>{t.sessionCount}</td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)' }}>{f0(t.td)}m</td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)' }}>{f0(t.hid)}m</td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)' }}>{f0(t.sa)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
